import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { C } from '../theme';

const BLANK = {
  company_name:   '',
  contact_person: '',
  contact_number: '',
  email:          '',
  gst_number:     '',
  notes:          '',
};

export default function Vendors() {
  const [vendors,  setVendors]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [stFilter, setStFilter] = useState('');

  const [modal,  setModal]  = useState(null);   // 'add'|'edit'|'delete'
  const [selVnd, setSelVnd] = useState(null);
  const [form,   setForm]   = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [errs,   setErrs]   = useState({});

  const [toast, setToast] = useState(null);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/api/vendors'); setVendors(r.data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openAdd = () => { setForm(BLANK); setErrs({}); setSelVnd(null); setModal('add'); };
  const openEdit = (v) => {
    setForm({
      company_name:   v.company_name   || '',
      contact_person: v.contact_person || '',
      contact_number: v.contact_number || '',
      email:          v.email          || '',
      gst_number:     v.gst_number     || '',
      notes:          v.notes          || '',
    });
    setErrs({}); setSelVnd(v); setModal('edit');
  };
  const openDelete = (v) => { setSelVnd(v); setModal('delete'); };
  const closeModal = () => { setModal(null); setSelVnd(null); setErrs({}); };

  const f = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrs(p => ({ ...p, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.company_name.trim())   e.company_name   = 'Company name is required.';
    if (!form.contact_person.trim()) e.contact_person = 'Contact person is required.';
    if (!form.contact_number.trim()) e.contact_number = 'Contact number is required.';
    return e;
  };

  const save = async () => {
    const e = validate();
    if (Object.keys(e).length) return setErrs(e);
    setSaving(true);
    try {
      const body = {
        company_name:   form.company_name.trim(),
        contact_person: form.contact_person.trim(),
        contact_number: form.contact_number.trim(),
        email:          form.email.trim()      || null,
        gst_number:     form.gst_number.trim() || null,
        notes:          form.notes.trim()      || null,
      };
      if (selVnd) await api.put(`/api/vendors/${selVnd.id}`, body);
      else         await api.post('/api/vendors', body);
      await loadAll();
      showToast('success', selVnd ? 'Vendor updated.' : 'Vendor added.');
      closeModal();
    } catch (ex) {
      showToast('error', ex?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/api/vendors/${selVnd.id}`);
      await loadAll();
      showToast('success', 'Vendor deleted.');
      closeModal();
    } catch (ex) {
      showToast('error', ex?.response?.data?.detail || 'Delete failed.');
    } finally { setSaving(false); }
  };

  const toggleStatus = async (v) => {
    const next = v.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/api/vendors/${v.id}`, { status: next });
      setVendors(prev => prev.map(x => x.id === v.id ? { ...x, status: next } : x));
    } catch { showToast('error', 'Status update failed.'); }
  };

  const filtered = vendors.filter(v => {
    const q = search.toLowerCase();
    return (
      (!q || v.company_name?.toLowerCase().includes(q)
          || v.vendor_id?.toLowerCase().includes(q)
          || v.contact_person?.toLowerCase().includes(q)
          || v.contact_number?.includes(q)
          || v.email?.toLowerCase().includes(q))
      && (!stFilter || v.status === stFilter)
    );
  });

  return (
    <Layout title="Vendors">

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 4000,
          background: toast.type === 'success' ? C.successBg : C.dangerBg,
          color:      toast.type === 'success' ? C.successText : C.dangerText,
          border:     `1px solid ${toast.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
          borderRadius: '10px', padding: '12px 20px', fontSize: '13px', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          {toast.type === 'success' ? '✓' : '⚠'} {toast.text}
        </div>
      )}

      {/* Summary */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '22px', flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { label: 'Total Vendors', val: vendors.length,                                       dot: C.primary },
          { label: 'Active',        val: vendors.filter(v => v.status === 'active').length,    dot: C.success },
          { label: 'Inactive',      val: vendors.filter(v => v.status === 'inactive').length,  dot: C.textSub },
        ].map(({ label, val, dot }) => (
          <div key={label} style={{
            background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px',
            padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot }} />
            <div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: C.textMain }}>{val}</div>
              <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 500 }}>{label}</div>
            </div>
          </div>
        ))}
        <button onClick={openAdd} style={{ ...btnPrimary, marginLeft: 'auto' }}>
          + Add Vendor
        </button>
      </div>

      {/* Table */}
      <div style={card}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder="Search company, contact, email…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '9px 14px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none' }}
          />
          <select value={stFilter} onChange={e => setStFilter(e.target.value)} style={selStyle}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {(search || stFilter) && (
            <button onClick={() => { setSearch(''); setStFilter(''); }} style={btnGhost}>Clear</button>
          )}
          <span style={{ fontSize: '12px', color: C.textSub }}>{filtered.length} / {vendors.length}</span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead>
              <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
                {['Vendor', 'Contact Person', 'Contact / Email', 'GST Number', 'Status', 'Actions'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={emptyCell}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} style={emptyCell}>
                  {vendors.length === 0 ? 'No vendors yet. Click "+ Add Vendor" to start.' : 'No results match the filters.'}
                </td></tr>
              ) : filtered.map((v, i) => (
                <tr key={v.id} style={{ background: i % 2 === 0 ? C.white : '#FFFBF5', borderBottom: `1px solid ${C.border}` }}>

                  {/* Vendor */}
                  <td style={td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
                        background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '15px', fontWeight: 800, color: '#fff',
                      }}>
                        {(v.company_name || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, color: C.textMain, fontSize: '13px' }}>{v.company_name}</div>
                        <div style={{ fontSize: '11px', color: C.primaryDeep, fontFamily: 'monospace', fontWeight: 600 }}>{v.vendor_id}</div>
                      </div>
                    </div>
                  </td>

                  {/* Contact Person */}
                  <td style={td}>
                    <span style={{ fontWeight: 600, color: C.textMain }}>{v.contact_person}</span>
                  </td>

                  {/* Contact / Email */}
                  <td style={td}>
                    <div style={{ fontSize: '13px', color: C.textMain, fontWeight: 600 }}>{v.contact_number}</div>
                    {v.email && <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>{v.email}</div>}
                  </td>

                  {/* GST */}
                  <td style={td}>
                    {v.gst_number
                      ? <span style={{
                          background: C.primaryLight, color: C.primaryDeep,
                          padding: '3px 10px', borderRadius: '6px',
                          fontSize: '12px', fontWeight: 600, fontFamily: 'monospace',
                        }}>{v.gst_number}</span>
                      : <span style={{ color: C.textSub, fontSize: '12px' }}>—</span>}
                  </td>

                  {/* Status */}
                  <td style={td}>
                    <button onClick={() => toggleStatus(v)} style={{
                      border: 'none', cursor: 'pointer', borderRadius: '20px',
                      padding: '5px 14px', fontSize: '11px', fontWeight: 600,
                      background: v.status === 'active' ? C.successBg : C.dangerBg,
                      color:      v.status === 'active' ? C.successText : C.dangerText,
                    }}>
                      {v.status === 'active' ? '● Active' : '○ Inactive'}
                    </button>
                  </td>

                  {/* Actions */}
                  <td style={td}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <SmBtn label="Edit"   color="#3B82F6" onClick={() => openEdit(v)} />
                      <SmBtn label="Delete" color={C.danger} onClick={() => openDelete(v)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {(modal === 'add' || modal === 'edit') && (
        <Overlay>
          <div style={modalBox}>
            <MHeader
              title={modal === 'add' ? 'Add Vendor' : `${selVnd?.company_name} — ${selVnd?.vendor_id}`}
              onClose={closeModal}
            />
            <div style={{ padding: '24px 28px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <FF label="Company Name *" error={errs.company_name}>
                  <FInput value={form.company_name} onChange={v => f('company_name', v)}
                    placeholder="Enter Company Name" err={!!errs.company_name} />
                </FF>
                <FF label="Contact Person *" error={errs.contact_person}>
                  <FInput value={form.contact_person} onChange={v => f('contact_person', v)}
                    placeholder="Enter Contact Person" err={!!errs.contact_person} />
                </FF>
                <FF label="Contact Number *" error={errs.contact_number}>
                  <FInput value={form.contact_number} onChange={v => f('contact_number', v)}
                    placeholder="Enter Contact Number" err={!!errs.contact_number} />
                </FF>
                <FF label="Email Address">
                  <FInput type="email" value={form.email} onChange={v => f('email', v)}
                    placeholder="Enter Email Address" />
                </FF>
                <FF label="GST Number">
                  <FInput value={form.gst_number} onChange={v => f('gst_number', v)}
                    placeholder="Enter GST Number" />
                </FF>
              </div>
              <FF label="Notes">
                <textarea
                  value={form.notes}
                  onChange={e => f('notes', e.target.value)}
                  placeholder="Any notes about this vendor…"
                  rows={3}
                  style={{
                    width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`,
                    borderRadius: '8px', fontSize: '13px', color: C.textMain,
                    outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit',
                  }}
                />
              </FF>
            </div>
            <MFooter onClose={closeModal} onSave={save} saving={saving}
              label={modal === 'add' ? '+ Add Vendor' : '💾 Update Vendor'} />
          </div>
        </Overlay>
      )}

      {/* ── Delete Modal ── */}
      {modal === 'delete' && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '400px' }}>
            <MHeader title="Delete Vendor" onClose={closeModal} />
            <div style={{ padding: '20px 28px' }}>
              <p style={{ fontSize: '13px', color: C.textMain, lineHeight: 1.6, margin: 0 }}>
                Delete <strong style={{ color: C.danger }}>{selVnd?.company_name}</strong>{' '}
                <span style={{ fontSize: '11px', color: C.textSub }}>({selVnd?.vendor_id})</span>?
                <br /><span style={{ fontSize: '12px', color: C.textSub }}>This cannot be undone.</span>
              </p>
            </div>
            <MFooter onClose={closeModal} onSave={confirmDelete} saving={saving} label="Delete" danger />
          </div>
        </Overlay>
      )}
    </Layout>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SmBtn({ label, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
      background: `${color}18`, color, border: `1px solid ${color}30`, cursor: 'pointer',
    }}>{label}</button>
  );
}

function FF({ label, error, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.textMain, marginBottom: '6px' }}>
        {label}
      </label>
      {children}
      {error && <div style={{ fontSize: '11px', color: C.danger, marginTop: '4px' }}>⚠ {error}</div>}
    </div>
  );
}

function FInput({ type = 'text', value, onChange, placeholder, err = false }) {
  const [focus, setFocus] = useState(false);
  return (
    <input type={type} value={value ?? ''} placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      style={{
        width: '100%', padding: '9px 12px', boxSizing: 'border-box',
        border: `1.5px solid ${err ? C.danger : focus ? C.primary : C.border}`,
        borderRadius: '8px', fontSize: '13px', color: C.textMain, outline: 'none',
      }}
    />
  );
}

function Overlay({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px',
    }}>{children}</div>
  );
}

function MHeader({ title, onClose }) {
  return (
    <div style={{
      padding: '22px 28px', borderBottom: `1px solid ${C.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '3px', height: '18px', background: C.primary, borderRadius: '2px' }} />
        <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>{title}</span>
      </div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
    </div>
  );
}

function MFooter({ onClose, onSave, saving, label, danger = false }) {
  return (
    <div style={{
      padding: '16px 28px', borderTop: `1px solid ${C.border}`,
      display: 'flex', justifyContent: 'flex-end', gap: '10px',
    }}>
      <button onClick={onClose} style={btnGhost}>Cancel</button>
      <button onClick={onSave} disabled={saving} style={danger ? btnDanger : btnPrimary}>
        {saving ? 'Saving…' : label}
      </button>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const card     = { background: C.white, borderRadius: '12px', padding: '22px', border: `1px solid ${C.border}` };
const modalBox = { background: C.white, borderRadius: '16px', width: '100%', maxWidth: '600px', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', border: `1px solid ${C.border}` };
const th       = { textAlign: 'left', padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' };
const td       = { padding: '13px 14px', fontSize: '13px', color: C.textMain, verticalAlign: 'middle' };
const emptyCell = { textAlign: 'center', padding: '52px', color: C.textSub, fontSize: '13px' };
const selStyle = { padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', background: C.white, cursor: 'pointer' };
const btnPrimary = {
  padding: '10px 20px', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
  color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700,
  cursor: 'pointer', boxShadow: `0 3px 10px rgba(245,166,35,0.3)`,
};
const btnGhost  = { padding: '10px 18px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' };
const btnDanger = { padding: '10px 18px', background: C.danger, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' };
